import { Grid, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material'
import React, { useContext, useEffect, useState } from 'react'
import globalStyles from "../../../assets/styles/global.module.scss";
import dashboardStyles from './../../../assets/styles/componentStyles/callAnalysisStyles/callAnalysisDashboard.module.scss';
import style from "../../../assets/styles/componentStyles/customApiStyles/customApiStyle.module.scss";
import { DataConnectorContext, DataConnectorContextpayload } from '../../../context/dataConnectorContext';
import { chatbotListInterface, DataConnectorAssignment, dataConnectorFunctionHeader, dataConnectorList, functionParameterData, globalMenuItemData } from '../../../models/axiosModel';
import deleteDoc from "../../../assets/deleteDoc.svg";
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { UserContext, UserContextPayload } from '../../../context/userContext';
import { useNavigate } from 'react-router-dom';
import IconsColor from '../../utility/iconsColor';
import { ChatbotContext, ChatbotContextPayload } from '../../../context/chatbotContext';
import { fontColorForMethod, formatDateAccordingToTimezoneUsingMoment, getValueOrDefault, hasAIChatbotManageAccess, hasCustomAPIPageManageAccess, hasVoiceAgentManagePermission, isAdmin, isSystemApi } from '../../../utils/constants';
import { getVoiceAgentAssignmentUniqueId, isVoiceAgentDataConnectorAssignment } from './assignVoiceAgentPopup';
import { useVoiceAgentStore } from '../../../store/voiceAgentStore';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { lightGreyColor, primaryColor } from '../../../utils/colors';
import { useGenericStore } from '../../../store/genericStore';
import { useShallow } from 'zustand/react/shallow';
import agent from '../../../services/api';
import GlobalMenu from '../../globalMenu';

const getRequestVariableParentId = (param: functionParameterData) => {
    const parentId = param?.parent_function_parameter_data_id as unknown;
    if (parentId === null || parentId === undefined || parentId === '') {
        return null;
    }
    return String(parentId);
};

const getRequestVariableKey = (param: functionParameterData, parent?: functionParameterData) => {
    const name = param?.function_parameter_name || '';
    if (!parent?.function_parameter_name) {
        return name;
    }

    const parentPrefix = `${parent.function_parameter_name}.`;
    if (name.startsWith(parentPrefix) && name.length > parentPrefix.length) {
        return name.slice(parentPrefix.length);
    }

    return name;
};

const buildRequestVariablePayload = (parameters: functionParameterData[] = []) => {
    const childrenByParentId = new Map<string, functionParameterData[]>();
    const roots: functionParameterData[] = [];

    parameters.forEach((param) => {
        if (!param?.function_parameter_name) {
            return;
        }

        const parentId = getRequestVariableParentId(param);
        if (parentId === null) {
            roots.push(param);
            return;
        }

        const children = childrenByParentId.get(parentId) ?? [];
        children.push(param);
        childrenByParentId.set(parentId, children);
    });

    const buildValue = (param: functionParameterData): unknown => {
        const typeName = (param.function_parameter_type_name || '').toLowerCase();
        const children = param.function_parameter_data_id != null
            ? (childrenByParentId.get(String(param.function_parameter_data_id)) ?? [])
            : [];

        if (typeName === 'array') {
            if (children.length === 0) {
                return [];
            }

            const item: Record<string, unknown> = {};
            children.forEach((child) => {
                const childKey = getRequestVariableKey(child, param);
                if (childKey) {
                    item[childKey] = buildValue(child);
                }
            });
            return [item];
        }

        if (typeName === 'object' || children.length > 0) { 
            const nested: Record<string, unknown> = {};
            children.forEach((child) => {
                const childKey = getRequestVariableKey(child, param);
                if (childKey) {
                    nested[childKey] = buildValue(child);
                }
            });
            return nested;
        }

        return '';
    };

    const payload: Record<string, unknown> = {};
    roots.forEach((root) => {
        const key = getRequestVariableKey(root);
        if (key) {
            payload[key] = buildValue(root);
        }
    });
    return payload;
};

const flattenRequestVariables = (value: Record<string, unknown>, prefix = ''): Array<[string, string]> => {
    return Object.entries(value).flatMap(([key, nested]) => {
        const nextKey = prefix ? `${prefix}.${key}` : key;

        if (nested !== null && typeof nested === 'object' && !Array.isArray(nested)) {
            return flattenRequestVariables(nested as Record<string, unknown>, nextKey);
        }

        if (Array.isArray(nested)) {
            return nested.flatMap((item, index) => {
                if (item !== null && typeof item === 'object') {
                    return flattenRequestVariables(item as Record<string, unknown>, `${nextKey}[${index}]`);
                }
                return [[`${nextKey}[${index}]`, String(item ?? '')]] as Array<[string, string]>;
            });
        }

        return [[nextKey, String(nested ?? '')]] as Array<[string, string]>;
    });
};

const appendQueryParamsToUrl = (url: string, payload: Record<string, unknown>) => {
    const queryString = flattenRequestVariables(payload)
        .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
        .join('&');

    if (!queryString) {
        return url;
    }

    return `${url}${url.includes('?') ? '&' : '?'}${queryString}`;
};

interface CustomApiTableProps {
    setShowDeleteConfirmation: React.Dispatch<React.SetStateAction<boolean>>,
    setSelectedDataConnector: React.Dispatch<React.SetStateAction<dataConnectorList | null>>,
    setShowAssignedChatbotPopup: React.Dispatch<React.SetStateAction<boolean>>,
    setShowAssignedVoiceAgentPopup: React.Dispatch<React.SetStateAction<boolean>>,
}

type CustomApiMenuItem = globalMenuItemData & { hideForSystemApi?: boolean }; 

const CustomApiTable: React.FC<CustomApiTableProps> = (props) => {

    const { setShowDeleteConfirmation, setSelectedDataConnector, setShowAssignedChatbotPopup, setShowAssignedVoiceAgentPopup} = props;
    const { dataConnectorList } = useContext(DataConnectorContext) as DataConnectorContextpayload;
    const { setFlagLoading, handleError, apiRoutesPermissionAssignment, userDetail } = useContext(UserContext) as UserContextPayload;
    const { GetChatbotListApi, chatbotList } = useContext(ChatbotContext) as ChatbotContextPayload;

    const voiceAgentList = useVoiceAgentStore((state) => state.voiceAgentList);
    const getVoiceAgentListAPI = useVoiceAgentStore((state) => state.getVoiceAgentListAPI);

    const { showError, showSuccess } = useGenericStore(useShallow((state) => ({
        showError: state.showError,
        showSuccess: state.showSuccess
    })))

    const innerHeight = (window.innerHeight - 150);
    const navigate = useNavigate()

    const [anchorEl, setAnchorEl] = useState<null | SVGSVGElement>(null);
    const [currentRowIndex, setCurrentRowIndex] = useState<number | null>(null);

    const handleMenuOpen = (event: React.MouseEvent<SVGSVGElement, MouseEvent>, rowIndex: number) => {
        event.stopPropagation();
        setCurrentRowIndex(rowIndex);
        setAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
        setCurrentRowIndex(null);
        setAnchorEl(null);
    };

    const isChatbotAssignment = (assignment: DataConnectorAssignment) => {
        return !isVoiceAgentDataConnectorAssignment(assignment);
    }

    const getAssignedChatbots = (dataConnector: dataConnectorList) => {
        return chatbotList?.filter((chatbot: chatbotListInterface) =>
            dataConnector.data_connector_assignment?.some(
                (connector: DataConnectorAssignment) =>
                    isChatbotAssignment(connector)
                    && connector.data_connector_assignment_unique_id?.toString() === chatbot.chatbot_id
            )
        ) || [];
    }

    const getAssignedVoiceAgents = (dataConnector: dataConnectorList) => {
        return (dataConnector.data_connector_assignment || [])
            .filter(isVoiceAgentDataConnectorAssignment)
            .map((assignment) => {
                const voiceAgentId = getVoiceAgentAssignmentUniqueId(assignment);
                const fromList = voiceAgentList?.find((agent) => agent.voice_agent_id === voiceAgentId);
                return {
                    voice_agent_id: voiceAgentId,
                    voice_agent_name: assignment.voice_agent_name || fromList?.voice_agent_name || voiceAgentId,
                };
            })
            .filter((agent) => agent.voice_agent_id);
    }

    const handleCopyCurl = async (dataConnector: dataConnectorList) => {
        if(!dataConnector || !dataConnector?.chatbot_data_connector_id){
            showError("custom API not found");
            return;
        }

        const connectorId = dataConnector?.chatbot_data_connector_id;
        setFlagLoading(prevState => ({ ...prevState, flagRoundLoader: true }));
        try {
            const res = await agent.getDataConnectorDetail.get(Number(connectorId));

            if(!res?.status){
                handleError(res);
                return;
            }

            const dataConnectorDetails = res?.data?.[0];
            const method = dataConnectorDetails?.function_method_name;
            const url = dataConnectorDetails?.chatbot_data_connector_url;
            const headers = dataConnectorDetails?.data_connector_functions?.data_connector_function_headers ?? [];
            const requestParameters = Array.isArray(
                dataConnectorDetails?.data_connector_functions?.data_connector_function_parameters?.function_parameter_data
            )
                ? dataConnectorDetails.data_connector_functions.data_connector_function_parameters.function_parameter_data
                : [];

            if (!method || !url) {
                showError("Unable to copy cURL");
                return;
            }

            const escapeSingleQuotes = (value: string) => String(value).replace(/'/g, `'\\''`);
            const requestPayload = buildRequestVariablePayload(requestParameters);
            const isPostMethod = method.trim().toUpperCase() === 'POST';
            const requestUrl = isPostMethod ? url : appendQueryParamsToUrl(url, requestPayload);
            const escapedUrl = escapeSingleQuotes(requestUrl);
            const headerLines: string[] = headers
                .filter((header: dataConnectorFunctionHeader) => header?.function_header_key)
                .map((header: dataConnectorFunctionHeader) => {
                    const key = escapeSingleQuotes(header.function_header_key);
                    const value = escapeSingleQuotes(header.function_header_value ?? '');
                    return `--header '${key}: ${value}'`;
                });

            const hasContentTypeHeader = headerLines.some((line) =>
                line.toLowerCase().includes("'content-type:")
            );
            if (isPostMethod && Object.keys(requestPayload).length > 0 && !hasContentTypeHeader) {
                headerLines.push(`--header 'content-type: application/json'`);
            }

            const curlLines = [
                `curl --location --request ${method} '${escapedUrl}'`,
                ...headerLines
            ];

            if (isPostMethod && Object.keys(requestPayload).length > 0) {
                const dataPayload = JSON.stringify(requestPayload, null, 4).replace(/'/g, `'\\''`);
                curlLines.push(`--data '${dataPayload}'`);
            }

            const curl = curlLines
                .map((line, index) => index === curlLines.length - 1 ? line : `${line} \\`)
                .join('\n');

            await navigator.clipboard.writeText(curl);
            showSuccess("cURL Copied to Clipboard Successfully!");
        } catch (err){
            showError("Unable to copy cURL");
            console.error("Unable to copy cURL", err);
        } finally {
            setFlagLoading(prevState => ({ ...prevState, flagRoundLoader: false }));
        }
    }

    const showChatbotColumn = hasAIChatbotManageAccess(apiRoutesPermissionAssignment, userDetail);
    const showVoiceAgentColumn = hasVoiceAgentManagePermission(apiRoutesPermissionAssignment, userDetail)
        || hasCustomAPIPageManageAccess(apiRoutesPermissionAssignment, userDetail);

    const menuItem: CustomApiMenuItem[] = [
        ...(showChatbotColumn ? [
            {
                type: "item" as const,
                label: "Assign Chatbot",
                icon: <IconsColor iconName="chatbot" fillColor="#514D53" />,
                hideForSystemApi: false,
                onClick: (dataConnector: dataConnectorList) => {
                    setSelectedDataConnector(dataConnector);
                    setShowAssignedChatbotPopup(true);
                }
            }
        ] : []),
        ...(showVoiceAgentColumn ? [
            {
                type: "item" as const,
                label: "Assign Voice Agent",
                icon: <IconsColor iconName="voiceAgent" fillColor="#514D53" width="18" height="18" />,
                hideForSystemApi: false,
                onClick: (dataConnector: dataConnectorList) => {
                    setSelectedDataConnector(dataConnector);
                    setShowAssignedVoiceAgentPopup(true);
                }
            }
        ] : []),
        ...(isAdmin(userDetail) ? [
            {
                type: "item",
                label: 'Copy cURL',
                icon: <ContentCopyIcon sx={{ color: lightGreyColor, fontSize: "18px" }} />,
                hideForSystemApi: true,
                onClick: (dataConnector: dataConnectorList) => handleCopyCurl(dataConnector)
            }
        ] as CustomApiMenuItem[] : []),
        {
            type: "item",
            label: "Delete",
            icon: <img alt="" src={deleteDoc} />,
            hideForSystemApi: true,
            onClick: (dataConnector: dataConnectorList) => {
                setSelectedDataConnector(dataConnector);
                setShowDeleteConfirmation(true);
            }
        }
    ]

    const getMenuItemsForRow = (dataConnector: dataConnectorList) => {
        const isSystem = isSystemApi(dataConnector?.chatbot_data_connector_function_path);
        const allowVoiceAgentAssignment = Boolean(dataConnector.flag_allow_voice_agent_assignment);
        return menuItem.filter((item) => {
            if (item.label?.toLowerCase() === "assign voice agent") {
                return !isSystem || allowVoiceAgentAssignment;
            }
            if (item.label?.toLowerCase() === "assign chatbot") {
                return !(isSystem && allowVoiceAgentAssignment);
            }
            return !(isSystem && item.hideForSystemApi);
        });
    }

    const getChatbotListApiCall = async () => {
        setFlagLoading(prevState => ({ ...prevState, flagRoundLoader: true }));
        const res = await GetChatbotListApi();
        if(!(res.status)){
            handleError(res);
        }
        setFlagLoading(prevState => ({ ...prevState, flagRoundLoader: false }));
    }

    const getVoiceAgentListApiCall = async () => {
        setFlagLoading(prevState => ({ ...prevState, flagRoundLoader: true }));
        const res = await getVoiceAgentListAPI();
        if (!(res.status)) {
            handleError(res);
        }
        setFlagLoading(prevState => ({ ...prevState, flagRoundLoader: false }));
    }

    useEffect(() => {
        if(!chatbotList && hasAIChatbotManageAccess(apiRoutesPermissionAssignment, userDetail)){
            getChatbotListApiCall();
        }
        if (!voiceAgentList && showVoiceAgentColumn) {
            getVoiceAgentListApiCall();
        }
    }, [])

    const sortedDataConnectorList = [...(dataConnectorList || [])].sort((a, b) => {
        const aIsSystemApi = isSystemApi(a?.chatbot_data_connector_function_path);
        const bIsSystemApi = isSystemApi(b?.chatbot_data_connector_function_path);

        if(aIsSystemApi !== bIsSystemApi){
            return aIsSystemApi ? -1 : 1;
        }

        if(!aIsSystemApi && !bIsSystemApi){
            return new Date(b?.last_updated_at_timestamp || 0).getTime() - new Date(a?.last_updated_at_timestamp || 0).getTime();
        }

        return 0;
    });

    return (
        <>
            <Grid item xs={12} sm={12} className={`${style.tableGridItem} ${globalStyles.fullwidth}`}>
                <TableContainer className={`${style.customAPITableContainer}`} sx={{ maxHeight: `${innerHeight}px !important` }}>
                    <Table stickyHeader>
                        <TableHead>
                            <TableRow>
                                <TableCell className={dashboardStyles.tableBodyHeadingFontStyle}>Name</TableCell>
                                <TableCell className={dashboardStyles.tableBodyHeadingFontStyle}>Description</TableCell>
                                <TableCell className={dashboardStyles.tableBodyHeadingFontStyle}>URL</TableCell>
                                <TableCell className={dashboardStyles.tableBodyHeadingFontStyle}>Method Name</TableCell>
                                {showChatbotColumn && 
                                    <TableCell className={dashboardStyles.tableBodyHeadingFontStyle}>Assigned Chatbot</TableCell>
                                }
                                {showVoiceAgentColumn &&
                                    <TableCell className={dashboardStyles.tableBodyHeadingFontStyle}>Assigned Voice Agent</TableCell>
                                }
                                <TableCell className={dashboardStyles.tableBodyHeadingFontStyle}>Updated at</TableCell>
                                {hasCustomAPIPageManageAccess(apiRoutesPermissionAssignment, userDetail) && 
                                    <TableCell className={`${dashboardStyles.tableBodyHeadingFontStyle} ${style.lastColumnSticky} ${style.tableHeaderMorevertCell}`}></TableCell>
                                }
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {(sortedDataConnectorList && sortedDataConnectorList?.length > 0) ?
                                sortedDataConnectorList.map((dataConnector: dataConnectorList, index: number) => {
                                    const assignedChatbots = getAssignedChatbots(dataConnector);
                                    const assignedVoiceAgents = getAssignedVoiceAgents(dataConnector);
                                    const rowMenuItems = getMenuItemsForRow(dataConnector);
                                    return (
                                        <TableRow key={index} className={`${dashboardStyles.cellRow} ${isSystemApi(dataConnector?.chatbot_data_connector_function_path) ? '' : globalStyles.clickable}`} onClick={() => { if(!isSystemApi(dataConnector?.chatbot_data_connector_function_path)) navigate(`/customAPI/createCustomAPI?connector_id=${dataConnector.chatbot_data_connector_id}`) }}>
                                            <TableCell className={`${dashboardStyles.tableBodySubHeadingFontStyle}`}>
                                                <Grid container alignItems={'center'} gap={1}>
                                                    {isSystemApi(dataConnector?.chatbot_data_connector_function_path) && 
                                                        ((dataConnector.flag_allow_voice_agent_assignment) ?
                                                        <IconsColor fillColor={primaryColor} iconName="voiceAgent" width="16" height="16" />
                                                        :
                                                        <IconsColor fillColor={primaryColor} iconName="chatbot" height={"16"} width={"16"} />)
                                                    }
                                                    {dataConnector.data_connector_functions?.data_connector_function_name || ""}
                                                    {isSystemApi(dataConnector?.chatbot_data_connector_function_path) && (
                                                        <Grid item boxShadow={`0px 0px 2px ${fontColorForMethod(dataConnector?.function_method_name)}`} padding={"1px 6px"} borderRadius={"5px"} color={fontColorForMethod(dataConnector?.function_method_name)} textAlign={"center"}>
                                                            {`System API ${dataConnector.flag_allow_voice_agent_assignment ? "- Voice Agent" : ""}`}
                                                        </Grid>
                                                    )}
                                                </Grid>
                                            </TableCell>
                                            <TableCell className={`${dashboardStyles.tableBodySubHeadingFontStyle} ${style.descriptionTableCell}`}>
                                                {isSystemApi(dataConnector?.chatbot_data_connector_function_path) && !dataConnector.flag_allow_voice_agent_assignment
                                                    ? "-"
                                                    : dataConnector.data_connector_functions?.data_connector_function_description || ""
                                                }
                                            </TableCell>
                                            <TableCell className={`${dashboardStyles.tableBodySubHeadingFontStyle} ${style.tableCell}`}>{getValueOrDefault(dataConnector.chatbot_data_connector_url)}</TableCell>
                                            <TableCell className={`${dashboardStyles.tableBodySubHeadingFontStyle}`}>{getValueOrDefault(dataConnector.function_method_name)}</TableCell>
                                            {showChatbotColumn && 
                                                <TableCell className={`${dashboardStyles.tableBodySubHeadingFontStyle}`}>
                                                    {assignedChatbots.length > 0 ?
                                                        <Grid container direction={"column"} gap={"8px"}>
                                                            {assignedChatbots.map((chatbot: chatbotListInterface) => {
                                                                return (
                                                                    <Grid item key={chatbot.chatbot_id} className={`${style.chatbotNameChip}`} onClick={(e) => {e.stopPropagation(); window.open(`${window.location.origin}/chatbot/viewChatbot?chatbot_id=${chatbot.chatbot_id}`, "_blank")}}>
                                                                        {chatbot.chatbot_name}
                                                                    </Grid>
                                                                );
                                                            })}
                                                        </Grid>
                                                        : "-"
                                                    }
                                                </TableCell>
                                            }
                                            {showVoiceAgentColumn &&
                                                <TableCell className={`${dashboardStyles.tableBodySubHeadingFontStyle}`}>
                                                    {assignedVoiceAgents.length > 0 ?
                                                        <Grid container direction={"column"} gap={"8px"}>
                                                            {assignedVoiceAgents.map((voiceAgent) => {
                                                                return (
                                                                    <Grid
                                                                        item
                                                                        key={voiceAgent.voice_agent_id}
                                                                        className={`${style.chatbotNameChip}`}
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            window.open(`${window.location.origin}/voiceagent/createvoiceagent?voice_agent_id=${voiceAgent.voice_agent_id}`, "_blank")
                                                                        }}
                                                                    >
                                                                        {voiceAgent.voice_agent_name}
                                                                    </Grid>
                                                                );
                                                            })}
                                                        </Grid>
                                                        : "-"
                                                    }
                                                </TableCell>
                                            }
                                            <TableCell className={`${dashboardStyles.tableBodySubHeadingFontStyle}`}>
                                                {(!isSystemApi(dataConnector?.chatbot_data_connector_function_path) || isAdmin(userDetail)) ? 
                                                    formatDateAccordingToTimezoneUsingMoment(dataConnector?.last_updated_at_timestamp, userDetail.timezone_hour)
                                                    : 
                                                    "-"
                                                }
                                            </TableCell>
                                            {hasCustomAPIPageManageAccess(apiRoutesPermissionAssignment, userDetail) && 
                                                <TableCell className={`${`${dashboardStyles.tableBodySubHeadingFontStyle}`} ${style.lastColumnSticky} ${style.moreVertIcon}`} onClick={(e) => e.stopPropagation()}>
                                                    {rowMenuItems.length > 0 &&
                                                        <>
                                                            <MoreVertIcon
                                                                className={`${globalStyles.clickable}`}
                                                                onClick={(e: React.MouseEvent<SVGSVGElement, MouseEvent>) => handleMenuOpen(e, dataConnector.chatbot_data_connector_id)}
                                                            />
                                                            <GlobalMenu
                                                                anchorEl={anchorEl}
                                                                handleMenuClose={handleMenuClose}
                                                                menuItemData={rowMenuItems}
                                                                open={Boolean(anchorEl) && dataConnector.chatbot_data_connector_id === currentRowIndex}
                                                                additionalField={dataConnector}
                                                                transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                                                                anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                                                            />
                                                        </>
                                                    }
                                                </TableCell>
                                            }
                                        </TableRow>
                                    );
                                })
                                :
                                <TableRow className={dashboardStyles.cellRow}>
                                    <TableCell colSpan={12} className={`${globalStyles.textCenter}`}>
                                        No Data Available
                                    </TableCell>
                                </TableRow>
                            }
                        </TableBody>
                    </Table>
                </TableContainer>
            </Grid>

            
        </>
    )
}

export default CustomApiTable
